// 登录 / 注册弹窗。登录成功 → setAuth（存 token+user）→ 关闭。
// 注册成功后自动登录（注册接口本身不返 token）。游客可不登录继续用（浏览/编辑/导出）。
// Google 登录：若后端 /auth/config 返回 google_client_id，加载 GIS 渲染「用 Google 继续」按钮。
import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { createPortal } from 'react-dom'
import i18n from '../i18n'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../api/client'
import { useAuthStore } from '../store/auth'

interface GoogleCredentialResponse {
  credential: string
}

// GIS client 的最小类型投影（避免引 @types）
interface GoogleGSI {
  accounts: {
    id: {
      initialize: (opts: unknown) => void
      renderButton: (el: HTMLElement, opts: unknown) => void
    }
  }
}

export default function AuthModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [code, setCode] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState('')
  const [googleClientId, setGoogleClientId] = useState('')
  const [emailLogin, setEmailLogin] = useState(true)
  const googleBtnRef = useRef<HTMLDivElement>(null)
  const setAuth = useAuthStore((s) => s.setAuth)

  // 拉 Google 配置（决定是否显示 Google 按钮）
  useEffect(() => {
    let alive = true
    api
      .authConfig()
      .then((c) => {
        if (alive) {
          setGoogleClientId(c.google_client_id || '')
          setEmailLogin(c.enable_email_login ?? true)
        }
      })
      .catch(() => {
        /* 静默：未配 Google 则不显示按钮 */
      })
    return () => {
      alive = false
    }
  }, [])

  const onGoogleCredential = async (resp: GoogleCredentialResponse) => {
    setError('')
    try {
      const r = await api.googleLogin(resp.credential)
      setAuth(r)
      onClose()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : t('auth.googleFail'))
    }
  }

  // 有 googleClientId → 加载 GIS 并渲染按钮
  useEffect(() => {
    if (!googleClientId) return
    const w = window as unknown as { google?: GoogleGSI }
    const init = () => {
      const g = (window as unknown as { google?: GoogleGSI }).google
      if (!g?.accounts?.id || !googleBtnRef.current) return
      g.accounts.id.initialize({ client_id: googleClientId, callback: onGoogleCredential })
      g.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        width: 320,
        locale: i18n.language === 'zh' ? 'zh-CN' : 'en',
      })
    }
    if (w.google) {
      init()
      return
    }
    if (document.getElementById('google-gsi')) {
      init()
      return
    }
    const s = document.createElement('script')
    s.id = 'google-gsi'
    s.src = 'https://accounts.google.com/gsi/client'
    s.async = true
    s.defer = true
    s.onload = init
    document.head.appendChild(s)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId])

  // 发送验证码 + 60s 倒计时
  const sendCode = async () => {
    if (!email || countdown > 0) return
    setError('')
    try {
      await api.sendCode(email)
      setCountdown(60)
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : t('auth.sendFail'))
    }
  }
  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown(countdown - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      if (mode === 'register') await api.register({ email, password, name, code })
      const resp = await api.login({ email, password })
      setAuth(resp)
      onClose()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : t('auth.opFail'))
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal modal-auth" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{emailLogin ? (mode === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')) : t('auth.loginTitle')}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="close">
            ✕
          </button>
        </div>
        <form className="modal-body" onSubmit={submit}>
          {!emailLogin && googleClientId && (
            <div className="auth-google-only">
              <div className="auth-welcome">🎬 Visword</div>
              <p className="auth-welcome-sub">{t('auth.googleOnlyHint')}</p>
            </div>
          )}
          {emailLogin && (
          <div className="auth-tabs">
            <button
              type="button"
              className={`auth-tab${mode === 'login' ? ' active' : ''}`}
              onClick={() => setMode('login')}
            >
              {t('auth.loginTab')}
            </button>
            <button
              type="button"
              className={`auth-tab${mode === 'register' ? ' active' : ''}`}
              onClick={() => setMode('register')}
            >
              {t('auth.registerTab')}
            </button>
          </div>
          )}

          {googleClientId && (
            <>
              <div className="auth-google" ref={googleBtnRef} />
              {emailLogin && (
                <div className="auth-divider">
                  <span>{t('auth.orEmail')}</span>
                </div>
              )}
            </>
          )}

          {emailLogin && (
            <>
          {mode === 'register' && (
            <div className="ex-field">
              <label>{t('auth.nickname')}</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={1}
                placeholder={t('auth.nicknamePh')}
              />
            </div>
          )}
          <div className="ex-field">
            <label>{t('auth.email')}</label>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('auth.emailPh')}
            />
          </div>
          <div className="ex-field">
            <label>{mode === 'register' ? t('auth.passwordMin8') : t('auth.password')}</label>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'register' ? 8 : 1}
              placeholder="••••••••"
            />
          </div>
          {mode === 'register' && (
            <div className="ex-field">
              <label>{t('auth.emailCode')}</label>
              <div className="ex-row">
                <input
                  className="input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  maxLength={6}
                  placeholder={t('auth.codePh')}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={sendCode}
                  disabled={countdown > 0 || !email}
                  style={{ flexShrink: 0 }}
                >
                  {countdown > 0 ? `${countdown}s` : t('auth.sendCode')}
                </button>
              </div>
            </div>
          )}
          {error && <div className="error-box">{error}</div>}
          <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={busy}>
            {busy ? '◐ …' : mode === 'login' ? t('auth.loginBtn') : t('auth.registerBtn')}
          </button>
            </>
          )}
        </form>
      </div>
    </div>,
    document.body,
  )
}
